import React, { useMemo } from 'react';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

/**
 * Dismissible filter chips for catalog toolbars (`CatalogBrowseShell` trailing row).
 *
 * @typedef {Object} CatalogTopBarChip
 * @property {string} key
 * @property {string} label
 * @property {() => void} onDismiss
 * @property {string} [tint] — When set, chip uses `${tint}1F` background and matching text / dismiss icon. When omitted, neutral surface (e.g. company scope).
 * @property {string} [leadingIcon]
 * @property {string} [accessibilityLabel]
 *
 * @param {{ chips?: CatalogTopBarChip[], trailing?: import('react').ReactNode }} props
 */
function CatalogTopBarChips({ chips = [], trailing = null }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(), []);

  return (
    <View style={styles.toolbarRow}>
      <View style={[styles.activeFilters, { flex: 1 }]}>
        {chips.map((chip) => {
          const isAccent = !!chip.tint;
          const bg = isAccent ? `${chip.tint}1F` : colors.solid_color_tertiary;
          const fg = isAccent ? chip.tint : colors.text_color_secondary;
          const a11y = chip.accessibilityLabel || `Clear ${chip.label}`;
          return (
            <Pressable
              key={chip.key}
              onPress={chip.onDismiss}
              accessibilityRole="button"
              accessibilityLabel={a11y}
              style={[
                styles.activeChip,
                { backgroundColor: bg },
                Platform.OS === 'web' ? { cursor: 'pointer' } : null,
              ]}
            >
              {chip.leadingIcon ? (
                <Icon name={chip.leadingIcon} size={9} color={fg} />
              ) : null}
              {isAccent ? (
                <Text size="s" weight="semibold" style={{ color: fg }}>{chip.label}</Text>
              ) : (
                <Text size="s" weight="semibold" color="secondary">{chip.label}</Text>
              )}
              <Icon name="xmark" size={13} color={fg} />
            </Pressable>
          );
        })}
      </View>
      {trailing ? (
        <View style={styles.toolbarRight}>
          {trailing}
        </View>
      ) : null}
    </View>
  );
}

function createStyles() {
  return StyleSheet.create({
    toolbarRow: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      minWidth: 0,
      gap: 12,
      flexWrap: 'wrap',
    },
    activeFilters: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 4,
    },
    activeChip: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      paddingHorizontal: 12,
      height: 28,
      borderRadius: 6,
    },
    toolbarRight: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
  });
}

const CatalogTopBarChipsMemo = React.memo(CatalogTopBarChips);
CatalogTopBarChipsMemo.displayName = 'CatalogTopBarChips';

export default CatalogTopBarChipsMemo;
